import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Card, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { fieldsAPI, modulesAPI } from '../api/client';
import { FIELD_TYPES } from '../utils/constants';

const { TextArea } = Input;

const FooterManager = () => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moduleModalVisible, setModuleModalVisible] = useState(false);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [moduleForm] = Form.useForm();
  const [fieldForm] = Form.useForm();

  // 加载footer模块列表
  const loadModules = async () => {
    setLoading(true);
    try {
      const data = await modulesAPI.getAll({ page_code: 'footer' });
      setModules(data);
      if (data.length > 0 && !selectedModule) {
        setSelectedModule(data[0].id);
      }
    } catch (error) {
      message.error('加载页脚模块失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 加载字段列表
  const loadFields = async () => {
    if (!selectedModule) return;
    setLoading(true);
    try {
      const data = await fieldsAPI.getAll({ module_id: selectedModule });
      setFields(data);
    } catch (error) {
      message.error('加载字段列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    if (selectedModule) {
      loadFields();
    }
  }, [selectedModule]);

  // 模块相关操作
  const openModuleModal = (module = null) => {
    setEditingModule(module);
    if (module) {
      moduleForm.setFieldsValue(module);
    } else {
      moduleForm.resetFields();
      moduleForm.setFieldsValue({ page_code: 'footer' });
    }
    setModuleModalVisible(true);
  };

  const closeModuleModal = () => {
    setModuleModalVisible(false);
    setEditingModule(null);
    moduleForm.resetFields();
  };

  const handleModuleSubmit = async () => {
    try {
      const values = await moduleForm.validateFields();
      if (editingModule) {
        await modulesAPI.update(editingModule.id, values);
        message.success('更新页脚模块成功');
      } else {
        await modulesAPI.create(values);
        message.success('创建页脚模块成功');
      }
      closeModuleModal();
      loadModules();
    } catch (error) {
      if (error.errorFields) return;
      message.error('操作失败: ' + error.message);
    }
  };

  const handleModuleDelete = async (id) => {
    try {
      await modulesAPI.delete(id);
      message.success('删除页脚模块成功');
      if (selectedModule === id) {
        setSelectedModule(null);
      }
      loadModules();
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  // 字段相关操作
  const openFieldModal = (field = null) => {
    setEditingField(field);
    if (field) {
      fieldForm.setFieldsValue(field);
    } else {
      fieldForm.resetFields();
      fieldForm.setFieldsValue({ module_id: selectedModule });
    }
    setFieldModalVisible(true);
  };

  const closeFieldModal = () => {
    setFieldModalVisible(false);
    setEditingField(null);
    fieldForm.resetFields();
  };

  const handleFieldSubmit = async () => {
    try {
      const values = await fieldForm.validateFields();
      if (editingField) {
        await fieldsAPI.update(editingField.id, values);
        message.success('更新字段成功');
      } else {
        await fieldsAPI.create(values);
        message.success('创建字段成功');
      }
      closeFieldModal();
      loadFields();
    } catch (error) {
      if (error.errorFields) return;
      message.error('操作失败: ' + error.message);
    }
  };

  const handleFieldDelete = async (id) => {
    try {
      await fieldsAPI.delete(id);
      message.success('删除字段成功');
      loadFields();
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  const fieldColumns = [
    {
      title: '字段名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '字段类型',
      dataIndex: 'field_type',
      key: 'field_type',
      width: 100,
      render: (type) => {
        const fieldType = FIELD_TYPES.find(t => t.value === type);
        return fieldType ? fieldType.label : type;
      }
    },
    {
      title: '中文内容',
      dataIndex: 'content_zh',
      key: 'content_zh',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '英文内容',
      dataIndex: 'content_en',
      key: 'content_en',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '外链URL',
      dataIndex: 'redirect_url',
      key: 'redirect_url',
      width: 150,
      ellipsis: true,
      render: (url) => url || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => openFieldModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此字段吗？"
            onConfirm={() => handleFieldDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card 
        title="页脚模块管理" 
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModuleModal()}>
            创建模块
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Space wrap>
          {modules.map(module => (
            <Button
              key={module.id}
              type={selectedModule === module.id ? 'primary' : 'default'}
              onClick={() => setSelectedModule(module.id)}
            >
              {module.name_zh}
              <Popconfirm
                title="确定删除此模块吗？"
                description="删除模块将同时删除其下所有字段"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleModuleDelete(module.id);
                }}
                okText="确定"
                cancelText="取消"
              >
                <DeleteOutlined 
                  style={{ marginLeft: 8, color: '#ff4d4f' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            </Button>
          ))}
          {modules.length === 0 && (
            <div style={{ color: '#999' }}>暂无页脚模块，请先创建模块</div>
          )}
        </Space>
      </Card>

      {selectedModule && (
        <Card
          title={`${modules.find(m => m.id === selectedModule)?.name_zh || ''} - 字段管理`}
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openFieldModal()}>
              添加字段
            </Button>
          }
        >
          <Table
            columns={fieldColumns}
            dataSource={fields}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}

      {/* 模块编辑弹窗 */}
      <Modal
        title={editingModule ? '编辑页脚模块' : '创建页脚模块'}
        open={moduleModalVisible}
        onOk={handleModuleSubmit}
        onCancel={closeModuleModal}
        width={600}
      >
        <Form form={moduleForm} layout="vertical">
          <Form.Item name="page_code" hidden initialValue="footer">
            <Input />
          </Form.Item>

          <Form.Item
            name="name_zh"
            label="模块中文名称"
            rules={[{ required: true, message: '请输入模块中文名称' }]}
          >
            <Input placeholder="例如：关于我们、联系方式、社交媒体" />
          </Form.Item>

          <Form.Item
            name="name_en"
            label="模块英文名称"
            rules={[{ required: true, message: '请输入模块英文名称' }]}
          >
            <Input placeholder="例如：About Us、Contact、Social Media" />
          </Form.Item>

          <Form.Item
            name="code"
            label="模块代码"
            rules={[
              { required: true, message: '请输入模块代码' },
              { pattern: /^[a-z0-9_-]+$/, message: '只能包含小写字母、数字、下划线和短横线' }
            ]}
          >
            <Input placeholder="例如：about_us、contact、social_media" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 字段编辑弹窗 */}
      <Modal
        title={editingField ? '编辑字段' : '创建字段'}
        open={fieldModalVisible}
        onOk={handleFieldSubmit}
        onCancel={closeFieldModal}
        width={900}
      >
        <Form form={fieldForm} layout="vertical">
          <Form.Item name="module_id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label="字段名称"
            rules={[{ required: true, message: '请输入字段名称' }]}
          >
            <Input placeholder="例如：公司地址、联系电话、微信号" />
          </Form.Item>

          <Form.Item
            name="field_type"
            label="字段类型"
            rules={[{ required: true, message: '请选择字段类型' }]}
          >
            <Select placeholder="请选择类型">
              {FIELD_TYPES.map(type => (
                <Select.Option key={type.value} value={type.value}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="redirect_url"
            label="外链URL（可选）"
            rules={[{ type: 'url', message: '请输入有效的URL' }]}
          >
            <Input placeholder="例如：https://weibo.com/xxx" />
          </Form.Item>

          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Form.Item
              name="content_zh"
              label="中文内容"
              style={{ marginBottom: 0 }}
            >
              <TextArea rows={4} placeholder="请输入中文内容" />
            </Form.Item>

            <Form.Item
              name="content_en"
              label="英文内容"
              style={{ marginBottom: 0 }}
            >
              <TextArea rows={4} placeholder="Please enter English content" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default FooterManager;







